import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { setAuthFromToken, getCurrentUser } from '../../features/auth/authSlice';

/**
 * Bootstraps auth for mobile WebView embed routes.
 * Expects ?token=<Bearer auth token> from the mobile app.
 * No backend bootstrap/exchange — same token the app already uses for API calls.
 */
export default function EmbedRoute({ children }) {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      const tokenFromUrl = searchParams.get('token');
      const existingToken = localStorage.getItem('auth_token');
      const token = tokenFromUrl || existingToken;

      if (!token) {
        if (!cancelled) {
          setStatus('error');
          setErrorMessage('Missing auth token. Open this page from the mobile app with ?token=...');
        }
        return;
      }

      dispatch(setAuthFromToken({ token }));

      // Strip token from the URL so it does not linger in history/logs.
      if (tokenFromUrl) {
        const next = new URLSearchParams(searchParams);
        next.delete('token');
        setSearchParams(next, { replace: true });
      }

      try {
        await dispatch(getCurrentUser()).unwrap();
        if (!cancelled) setStatus('ready');
      } catch (error) {
        if (cancelled) return;
        const message =
          error?.message ||
          error?.error ||
          'Invalid or expired auth token.';
        setStatus('error');
        setErrorMessage(typeof message === 'string' ? message : 'Invalid or expired auth token.');
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
    // Run once on mount for the initial token; intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  if (status === 'loading') {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Starting embed session...</span>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100 p-4">
        <div className="text-center" style={{ maxWidth: 420 }}>
          <h1 className="h5 mb-2">Unable to open editor</h1>
          <p className="text-muted mb-0">{errorMessage}</p>
        </div>
      </div>
    );
  }

  return children;
}
